import Decimal from "decimal.js";
import {
  ignoreSafeParseDecimal,
  computeCanolaStepFutures,
  isCanolaContractWithFuturesRows,
} from "../OtcBearState";
import {
  computeOtcFormWarnings,
  MarginWarningField,
  OtcFormWarnings,
} from "../contract-form-v2/warnings";
import { getOtcLayerFieldDef } from "./fieldRegistry";
import { OtcLayerFormValues } from "./snapshotToForm";

/**
 * Identifies the adjustment layer being edited so derived margin amounts can be
 * computed against the correct quantity basis. For an exit layer's OWN slot the
 * basis is `exit_quantity`; every other row uses contract (remaining) quantity.
 */
export interface OtcLayerMarginContext {
  amendment_type: string | null | undefined;
  adjustment_slot: number | null | undefined;
}

/**
 * Labels for backend-derived margin fields that drive warnings but are not
 * rendered as editable inputs in v3 (and therefore are not in the v3 field
 * registry). Used only for the warning dialog/tooltip copy.
 */
const DERIVED_MARGIN_LABELS: Record<string, string> = {
  margin: "Margin",
  margin_amount: "Margin Amount",
  adj_margin: "Adj Margin",
  adj_margin_amount: "Adj Margin Amount",
  adj_margin_two: "Adj Margin 2",
  adj_margin_amount_two: "Adj Margin Amount 2",
  adj_margin_three: "Adj Margin 3",
  adj_margin_amount_three: "Adj Margin Amount 3",
  expected_contract_futures: "Calculated Step 3 Futures",
  expected_contract_futures_step_one: "Calculated Step 1 Futures",
  expected_contract_futures_step_two: "Calculated Step 2 Futures",
  expected_contract_futures_double_up: "Calculated DU Futures",
  futures_margin: "Step 3 Futures Margin",
  futures_margin_step_one: "Step 1 Futures Margin",
  futures_margin_step_two: "Step 2 Futures Margin",
  futures_margin_double_up: "Double Up Futures Margin",
  final_futures_price_margin: "Final Futures Margin",
};

/**
 * Per-unit margin: fee - premium.
 */
function computeMargin(v: OtcLayerFormValues): string {
  const fee = ignoreSafeParseDecimal((v.contract_fee as string) || 0);
  const premium = ignoreSafeParseDecimal((v.option_premium as string) || 0);
  return fee.sub(premium).toString();
}

function computeMarginAmount(v: OtcLayerFormValues): string {
  const fee = ignoreSafeParseDecimal((v.contract_fee as string) || 0);
  const premium = ignoreSafeParseDecimal((v.option_premium as string) || 0);
  const qty = ignoreSafeParseDecimal((v.quantity as string) || 0);
  return fee.sub(premium).times(qty).toString();
}

/**
 * Per-unit adjustment margin: adj_fee - adj_premium.
 */
function computeAdjMargin(
  v: OtcLayerFormValues,
  feeKey: keyof OtcLayerFormValues,
  premiumKey: keyof OtcLayerFormValues,
): string {
  const fee = v[feeKey];
  const premium = v[premiumKey];
  if (!fee || !premium) return "";
  return ignoreSafeParseDecimal(fee as string | number)
    .sub(ignoreSafeParseDecimal(premium as string | number))
    .toString();
}

function computeAdjMarginAmount(
  v: OtcLayerFormValues,
  feeKey: keyof OtcLayerFormValues,
  premiumKey: keyof OtcLayerFormValues,
  quantityBasis: Decimal,
): string {
  const fee = v[feeKey];
  const premium = v[premiumKey];
  if (!fee || !premium) return "";
  return ignoreSafeParseDecimal(fee as string | number)
    .sub(ignoreSafeParseDecimal(premium as string | number))
    .times(quantityBasis)
    .toString();
}

/**
 * Resolves the quantity basis for a given adjustment slot's derived margin
 * amount. An exit layer's OWN slot uses `exit_quantity`; every other slot (and
 * non-exit layers) uses the contract remaining `quantity`.
 */
function quantityBasisForAdjSlot(
  v: OtcLayerFormValues,
  slot: number,
  marginContext: OtcLayerMarginContext | undefined,
): Decimal {
  const isExitOwnedSlot =
    marginContext?.amendment_type === "exit" &&
    marginContext.adjustment_slot === slot;
  const basisRaw = isExitOwnedSlot ? v.exit_quantity : v.quantity;
  return ignoreSafeParseDecimal((basisRaw as string) || 0);
}

function futuresMarginFor(
  v: OtcLayerFormValues,
  stepKey: keyof OtcLayerFormValues,
  actualKey: keyof OtcLayerFormValues,
): string {
  if (
    !isCanolaContractWithFuturesRows({
      commodity: v.commodity as string | undefined,
      contract_type: v.contract_type as string | undefined,
    })
  ) {
    return "";
  }
  const { futures_margin } = computeCanolaStepFutures({
    stepValue: v[stepKey] as string | number | undefined,
    implied_fx_amount: v.implied_fx_amount as string | number | undefined,
    actualContractFutures: v[actualKey] as string | number | undefined,
    quantity: v.quantity as string | number | undefined,
  });
  return futures_margin.isZero() ? "" : futures_margin.toFixed(4);
}

/**
 * Computes the expected (calculated) contract futures for a given step.
 * Formula: (step / 2204.62) * implied_fx_amount.
 * Returns "" when the contract is not canola or values are missing.
 */
function expectedContractFuturesFor(
  v: OtcLayerFormValues,
  stepKey: keyof OtcLayerFormValues,
): string {
  if (
    !isCanolaContractWithFuturesRows({
      commodity: v.commodity as string | undefined,
      contract_type: v.contract_type as string | undefined,
    })
  ) {
    return "";
  }
  const { expected_contract_futures } = computeCanolaStepFutures({
    stepValue: v[stepKey] as string | number | undefined,
    implied_fx_amount: v.implied_fx_amount as string | number | undefined,
    actualContractFutures: 0,
    quantity: v.quantity as string | number | undefined,
  });
  return expected_contract_futures.isZero() ? "" : expected_contract_futures.toFixed(4);
}

function computeFinalFuturesPriceMargin(v: OtcLayerFormValues): string {
  const expectedRaw = v.final_futures_expected_price;
  const priceRaw = v.final_futures_price;
  const quantityRaw = v.quantity;
  const isEmpty = (s: unknown): boolean =>
    s === undefined || s === null || (typeof s === "string" && s.trim() === "");
  if (isEmpty(expectedRaw) || isEmpty(priceRaw) || isEmpty(quantityRaw)) return "";
  const expected = ignoreSafeParseDecimal(expectedRaw as string | number);
  const price = ignoreSafeParseDecimal(priceRaw as string | number);
  const quantity = ignoreSafeParseDecimal(quantityRaw as string | number);
  const result = expected.sub(price).times(quantity);
  return result.isZero() ? "" : result.toFixed(4);
}

/**
 * Recompute backend-derived margin/futures fields from the current form state
 * so the shared warning detector can evaluate them. v3 never sends these to the
 * API (the backend recomputes), but warnings still depend on the resulting
 * contract values, so they must be derived for the review check.
 *
 * Also used by the form's "Derived Values" display section so users can
 * visualize how the math works out for premium, fee, and margin.
 */
export function withDerivedMarginValues(
  values: OtcLayerFormValues,
  marginContext?: OtcLayerMarginContext,
): Record<string, unknown> {
  return {
    ...values,
    margin: computeMargin(values),
    margin_amount: computeMarginAmount(values),
    adj_margin: computeAdjMargin(
      values,
      "adj_contract_fee",
      "adj_option_premium",
    ),
    adj_margin_amount: computeAdjMarginAmount(
      values,
      "adj_contract_fee",
      "adj_option_premium",
      quantityBasisForAdjSlot(values, 1, marginContext),
    ),
    adj_margin_two: computeAdjMargin(
      values,
      "adj_contract_fee_two",
      "adj_option_premium_two",
    ),
    adj_margin_amount_two: computeAdjMarginAmount(
      values,
      "adj_contract_fee_two",
      "adj_option_premium_two",
      quantityBasisForAdjSlot(values, 2, marginContext),
    ),
    adj_margin_three: computeAdjMargin(
      values,
      "adj_contract_fee_three",
      "adj_option_premium_three",
    ),
    adj_margin_amount_three: computeAdjMarginAmount(
      values,
      "adj_contract_fee_three",
      "adj_option_premium_three",
      quantityBasisForAdjSlot(values, 3, marginContext),
    ),
    // Canola expected (calculated) contract futures per step
    expected_contract_futures: expectedContractFuturesFor(values, "step_three"),
    expected_contract_futures_step_one: expectedContractFuturesFor(values, "step_one"),
    expected_contract_futures_step_two: expectedContractFuturesFor(values, "step_two"),
    expected_contract_futures_double_up: expectedContractFuturesFor(values, "double_up"),
    // Canola futures margin per step
    futures_margin: futuresMarginFor(values, "step_three", "actual_contract_futures"),
    futures_margin_step_one: futuresMarginFor(
      values,
      "step_one",
      "actual_contract_futures_step_one",
    ),
    futures_margin_step_two: futuresMarginFor(
      values,
      "step_two",
      "actual_contract_futures_step_two",
    ),
    futures_margin_double_up: futuresMarginFor(
      values,
      "double_up",
      "actual_contract_futures_double_up",
    ),
    final_futures_price_margin: computeFinalFuturesPriceMargin(values),
  };
}

/**
 * Computes OTC review warnings for the current layer form state, reusing the
 * shared (pure) v2 warning detector.
 *
 * The full contract scalar state lives in `values` (the v3 form is seeded from
 * the whole layer snapshot, then only owned fields are rendered/edited), so the
 * merged state needed for warnings is already present. Derived margin/futures
 * fields are recomputed here before detection.
 */
export function computeOtcLayerWarnings(
  values: OtcLayerFormValues,
  marginContext?: OtcLayerMarginContext,
): OtcFormWarnings {
  const evaluated = withDerivedMarginValues(values, marginContext);

  return computeOtcFormWarnings({
    // The shared detector reads only scalar string fields off this object.
    values: evaluated as never,
    // Step warnings: evaluate any step the contract carries; empty values are
    // skipped by the detector. Margin fields: always considered (empties are
    // skipped). v3 does not collapse rows, so there is no hidden-row state.
    isFieldVisible: () => true,
    isMarginFieldRendered: () => true,
    getFieldLabel: (name: string) =>
      getOtcLayerFieldDef(name)?.label ??
      DERIVED_MARGIN_LABELS[name as MarginWarningField] ??
      name,
  });
}
